import React, { useState, useRef, useEffect } from "react";

const GoogleMap = ({
  style,
  className,
  children,
  isDrawing,
  locationQuery,
  radius,
  setLocationQuery,
  onClick,
  areaGeometry,
}) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [listeners, setListeners] = useState([]);
  const [drawingManager, setDrawingManager] = useState(null);
  const [shape, setShape] = useState(null);

  const mapOptions = {
    zoom: 11,
    center: { lat: 33.773, lng: -112.3321 },
    mapTypeId: "roadmap",
    disableDefaultUI: true,
    clickableIcons: false,
  };

  const addListener = (listener) => {
    setListeners([...listeners, listener]);
  };

  const createMap = () => {
    const m = new window.google.maps.Map(mapRef.current, mapOptions);
    const newListeners = [
      window.google.maps.event.addListener(m, "click", onClick), // Saving, was used for deselecting a marker
    ];

    setListeners(newListeners);
    setDrawingManager(createDrawingManager(window.google, m, setLocationQuery, addListener));

    m.controls[window.google.maps.ControlPosition.RIGHT_TOP].push(zoomies(m));

    setMap(m);
  };

  const setUpArea = () => {
    map.data.addGeoJson({
      type: "Feature",
      geometry: areaGeometry,
    });

    map.data.setStyle({
      ...baseShapeStyle,
      editable: false,
      draggable: false,
      clickable: false,
    });

    const bounds = new window.google.maps.LatLngBounds();
    map.data.forEach((f) => {
      f.getGeometry().forEachLatLng((p) => {
        bounds.extend(p);
      });
    });

    map.fitBounds(bounds);
  };

  const setUpDrawing = () => {
    const polygon = new window.google.maps.Polygon({
      ...baseShapeStyle,
      map,
      paths: locationQuery.polygon,
      editable: true,
      draggable: false,
      clickable: false,
    });

    const listener = google.maps.event.addListener(polygon.getPath(), "set_at", (e) => {
      setLocationQuery({ isRadius: false, polygon: convertMvcArray(polygon.getPath()) });
    });

    addListener(listener);

    const bounds = new window.google.maps.LatLngBounds();
    locationQuery.polygon.forEach((p) => {
      bounds.extend(p);
    });

    map.fitBounds(bounds);

    setShape(polygon);
  };

  const setUpRadius = () => {
    const circle = new window.google.maps.Circle({
      ...baseShapeStyle,
      map,
      center: locationQuery.location,
      radius: radius,
      clickable: false,
    });

    map.fitBounds(circle.getBounds());

    setShape(circle);
  };

  const setUpAddress = () => {
    map.setCenter(locationQuery.location);
    map.setZoom(16);
  };

  useEffect(() => {
    return () => {
      for (const listener of listeners || []) {
        listener.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (isDrawing) {
      drawingManager?.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    } else {
      drawingManager?.setDrawingMode(null);
      shape?.setMap(null);
    }
  }, [isDrawing]);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (shape) {
      shape.setMap(null);
    }

    map.data?.forEach((feature) => {
      map.data.remove(feature);
    });

    if (!locationQuery) {
      return;
    }

    if (locationQuery.isRadius) {
      // Deprecated, is radius search on any google autocomplete value
      setUpRadius();
    } else if (locationQuery.isAddressSearch) {
      setUpAddress();
    } else if (locationQuery.isAreaSearch && areaGeometry) {
      setUpArea();
    } else if (locationQuery.polygon?.length) {
      setUpDrawing();
    }
  }, [locationQuery, areaGeometry, map, radius]);

  useEffect(() => {
    if (!mapRef.current || map) {
      return;
    }

    const load = async () => {
      if (window.google) {
        createMap();
        return;
      }

      await sleep(10);

      return load();
    };

    load();
  }, [mapRef, map]);

  return (
    <div ref={mapRef} className={className} style={style}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { map });
        }
      })}
    </div>
  );
};

export default GoogleMap;

const createDrawingManager = (google, map, onPolygonComplete, addListener) => {
  const drawingManager = new google.maps.drawing.DrawingManager({
    drawingMode: null,
    drawingControl: false,
    polygonOptions: {
      strokeColor: "#666666",
      strokeWeight: 2,
      fillColor: "#000000",
      fillOpacity: 0.1,
      zIndex: 1,
      editable: true,
      clickable: false,
    },
  });

  drawingManager.setMap(map);

  const listener = google.maps.event.addListener(drawingManager, "polygoncomplete", (poly) => {
    poly.setMap(null);
    drawingManager.setDrawingMode(null);

    onPolygonComplete({ isRadius: false, polygon: convertMvcArray(poly.getPath()) });
  });

  addListener(listener);

  return drawingManager;
};

const convertMvcArray = (array) => {
  const converted = [];
  array.forEach((ele, _) => {
    converted.push({ lat: ele.lat(), lng: ele.lng() });
  });

  return converted;
};

const zoomies = (map) => {
  const zoomDiv = document.createElement("div");

  zoomDiv.style.display = "flex";
  zoomDiv.style.flexDirection = "column";
  zoomDiv.style.border = "1px solid #c2c2c2";
  zoomDiv.style.borderRadius = "4px";
  zoomDiv.style.backgroundColor = "#fff";
  zoomDiv.style.color = "#666666";
  zoomDiv.style.cursor = "pointer";
  zoomDiv.style.marginRight = "12px";
  zoomDiv.style.marginTop = "18px";

  const plus = makeSvg(PlusPath, "Zoom In", true);
  const minus = makeSvg(MinusPath, "Zoom Out");

  zoomDiv.appendChild(plus);
  zoomDiv.appendChild(minus);

  plus.addEventListener("click", () => {
    map.setZoom(map.zoom + 1);
  });

  minus.addEventListener("click", () => {
    map.setZoom(map.zoom - 1);
  });

  return zoomDiv;
};

const makeSvg = (path, title, hasBorder = false) => {
  const div = document.createElement("div");
  div.title = title;
  div.classList.add("zoom-button");
  div.style.padding = "10px";
  div.style.height = "36px"; // Getting a random 2~px addition to height from i don't know where otherwise
  if (hasBorder) {
    div.style.borderBottom = "1px solid #c2c2c2";
  }

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const svgPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

  svgPath.setAttributeNS(null, "d", path);
  svg.style.overflow = "visible !important";
  svg.style.width = "16px";
  svg.style.height = "16px";
  svg.setAttributeNS(null, "viewBox", "0 0 16 16");
  svg.setAttributeNS(null, "fill", "currentColor");
  svg.appendChild(svgPath);
  div.appendChild(svg);

  return div;
};

const PlusPath =
  "M16 8a1.23 1.23 0 0 1-1.231 1.231H9.231v5.538a1.23 1.23 0 1 1-2.462 0V9.231H1.231a1.23 1.23 0 1 1 0-2.461h5.538V1.232a1.23 1.23 0 1 1 2.462 0V6.77h5.538C15.45 6.77 16 7.32 16 8z";
const MinusPath = "M13.5 9h-11a1 1 0 1 1 0-2h11a1 1 0 1 1 0 2z";

const sleep = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const baseShapeStyle = {
  strokeColor: "#666666",
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: "#666666",
  fillOpacity: 0.1,
};
